import { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { Card, CardHeader, Spinner, Button, Alert } from '../../components/UI';
import {
  Client,
  NetworkAnalysis,
  Ec2FlowLogFinding,
  SuggestedSgRule,
} from '../../types';
import { getClients } from '../../services/clients';
import {
  startNetworkAnalysis,
  getNetworkAnalysesByClient,
  getNetworkAnalysis,
} from '../../services/network';
import {
  ShieldCheckIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  ServerIcon,
  LockClosedIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

type Route = '/' | '/callback' | '/reports' | '/network';

interface NetworkPageProps {
  onNavigate?: (route: Route) => void;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    COMPLETED: { label: 'Completado', cls: 'bg-green-100 text-green-800', icon: <CheckCircleIcon className="h-4 w-4" /> },
    RUNNING: { label: 'En ejecución', cls: 'bg-blue-100 text-blue-800', icon: <ClockIcon className="h-4 w-4" /> },
    PENDING: { label: 'Pendiente', cls: 'bg-gray-100 text-gray-700', icon: <ClockIcon className="h-4 w-4" /> },
    FAILED: { label: 'Fallido', cls: 'bg-red-100 text-red-800', icon: <XCircleIcon className="h-4 w-4" /> },
  };
  const s = map[status] || map.PENDING;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>
      {s.icon}
      {s.label}
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
      title="Copiar"
    >
      <ClipboardDocumentIcon className="h-4 w-4" />
      {copied ? 'Copiado' : 'Copiar'}
    </button>
  );
}

const reasonLabel: Record<SuggestedSgRule['reason'], { text: string; cls: string }> = {
  NO_TRAFFIC_OBSERVED: { text: 'Sin tráfico — eliminar', cls: 'bg-red-50 text-red-700 border-red-200' },
  OBSERVED_EXTERNAL: { text: 'Tráfico externo — restringir a orígenes', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  OBSERVED_INTERNAL_ONLY: { text: 'Solo interno — restringir a VPC', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
};

function SuggestedRuleCard({ rule }: { rule: SuggestedSgRule }) {
  const badge = reasonLabel[rule.reason];
  return (
    <div className="border rounded-lg p-3 bg-gray-50">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium" style={{ color: '#1B2B6B' }}>
            {rule.securityGroupName} ({rule.securityGroupId})
          </span>
          <span className="text-xs text-gray-500">
            puerto {String(rule.port)}/{rule.protocol}
          </span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.text}</span>
      </div>
      <p className="text-sm text-gray-600 mt-2">{rule.explanation}</p>

      {rule.recommendedCidrs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {rule.recommendedCidrs.slice(0, 12).map((c) => (
            <span key={c} className="text-xs font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5 text-gray-700">
              {c}
            </span>
          ))}
          {rule.recommendedCidrs.length > 12 && (
            <span className="text-xs text-gray-500">+{rule.recommendedCidrs.length - 12} más</span>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2">
        <div className="bg-gray-900 rounded-md p-2 overflow-x-auto">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wide text-gray-400">1. Quitar regla abierta</span>
            <CopyButton text={rule.revokeCli} />
          </div>
          <code className="text-xs text-green-300 whitespace-pre-wrap break-all">{rule.revokeCli}</code>
        </div>
        {rule.authorizeCli.length > 0 && (
          <div className="bg-gray-900 rounded-md p-2 overflow-x-auto">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wide text-gray-400">2. Autorizar regla restringida</span>
              <CopyButton text={rule.authorizeCli.join('\n')} />
            </div>
            <code className="text-xs text-green-300 whitespace-pre-wrap break-all">
              {rule.authorizeCli.join('\n')}
            </code>
          </div>
        )}
      </div>
    </div>
  );
}

function InstanceFinding({ finding }: { finding: Ec2FlowLogFinding }) {
  const [open, setOpen] = useState(false);
  const hasSuggestions = finding.suggestedRules.length > 0;

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-3">
          <ServerIcon className="h-5 w-5 text-gray-400" />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1B2B6B' }}>
              {finding.instanceName || finding.instanceId}
            </p>
            <p className="text-xs text-gray-500">
              {finding.instanceId} · {finding.privateIpAddresses.join(', ') || 'sin IP privada'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {finding.openToWorldPorts.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
              <GlobeAltIcon className="h-3.5 w-3.5" />
              {finding.openToWorldPorts.length} abierto(s) al mundo
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            {finding.observedConnections.length} puerto(s) con tráfico
          </span>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          {/* Observed connections table */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Conexiones entrantes observadas
            </h4>
            {finding.observedConnections.length === 0 ? (
              <p className="text-sm text-gray-500">Sin tráfico entrante aceptado en la ventana analizada.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="py-1.5 pr-4">Puerto</th>
                      <th className="py-1.5 pr-4">Protocolo</th>
                      <th className="py-1.5 pr-4">Conexiones</th>
                      <th className="py-1.5 pr-4">Orígenes</th>
                      <th className="py-1.5 pr-4">Externo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finding.observedConnections.map((c) => (
                      <tr key={`${c.port}-${c.protocol}`} className="border-b border-gray-50">
                        <td className="py-1.5 pr-4 font-mono">{c.port}</td>
                        <td className="py-1.5 pr-4">{c.protocol}</td>
                        <td className="py-1.5 pr-4">{c.connectionCount.toLocaleString()}</td>
                        <td className="py-1.5 pr-4">{c.distinctSourceCount}</td>
                        <td className="py-1.5 pr-4">
                          {c.hasExternalSource ? (
                            <span className="text-amber-600">Sí</span>
                          ) : (
                            <span className="text-green-600">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Suggested rules */}
          {hasSuggestions && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Reglas de Security Group sugeridas
              </h4>
              <div className="space-y-2">
                {finding.suggestedRules.map((r, i) => (
                  <SuggestedRuleCard key={`${r.securityGroupId}-${r.port}-${i}`} rule={r} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function NetworkPage({ onNavigate }: NetworkPageProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [analyses, setAnalyses] = useState<NetworkAnalysis[]>([]);
  const [current, setCurrent] = useState<NetworkAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadClients = useCallback(async () => {
    try {
      const list = await getClients();
      setClients(list);
      if (list.length > 0 && !selectedClientId) {
        setSelectedClientId(list[0].id);
      }
    } catch {
      setError('No se pudieron cargar los clientes.');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  const loadAnalyses = useCallback(async (clientId: string) => {
    if (!clientId) return;
    try {
      const list = await getNetworkAnalysesByClient(clientId);
      setAnalyses(list);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (selectedClientId) {
      loadAnalyses(selectedClientId);
      setCurrent(null);
    }
  }, [selectedClientId, loadAnalyses]);

  // Poll the current analysis while it's running.
  useEffect(() => {
    if (!current || (current.status !== 'RUNNING' && current.status !== 'PENDING')) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const updated = await getNetworkAnalysis(current.id);
        setCurrent(updated);
        if (updated.status === 'COMPLETED' || updated.status === 'FAILED') {
          if (pollRef.current) clearInterval(pollRef.current);
          loadAnalyses(selectedClientId);
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [current, selectedClientId, loadAnalyses]);

  const handleStart = async () => {
    if (!selectedClientId) return;
    setStarting(true);
    setError(null);
    try {
      const analysis = await startNetworkAnalysis({ clientId: selectedClientId, windowDays: 90 });
      setCurrent(analysis);
      loadAnalyses(selectedClientId);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'No se pudo iniciar el análisis de red.');
    } finally {
      setStarting(false);
    }
  };

  const openAnalysis = async (id: string) => {
    try {
      const a = await getNetworkAnalysis(id);
      setCurrent(a);
    } catch {
      setError('No se pudo cargar el análisis.');
    }
  };

  const results = current?.results;

  return (
    <Layout onNavigate={onNavigate}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B2B6B' }}>
            Análisis de Red — VPC Flow Logs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Analiza las conexiones entrantes reales de cada EC2 (últimos 90 días) para restringir
            los Security Groups y eliminar reglas abiertas a 0.0.0.0/0.
          </p>
        </div>

        <Alert
          type="info"
          title="Antes de ejecutar"
          message="Este análisis lee VPC Flow Logs entregados a CloudWatch Logs. Requiere que los Flow Logs estén habilitados y hayan capturado la ventana a analizar. Consultar 90 días de logs con Logs Insights tiene costo por GB escaneado en la cuenta del cliente."
        />

        {error && <Alert type="error" message={error} />}

        {/* Launch panel */}
        <Card>
          <CardHeader title="Ejecutar nuevo análisis" icon={<ShieldCheckIcon className="h-5 w-5" />} />
          {loading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : (
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Cliente</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Ventana</label>
                <div className="text-sm text-gray-700 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                  90 días
                </div>
              </div>
              <Button onClick={handleStart} loading={starting} disabled={!selectedClientId}>
                <PlayIcon className="h-4 w-4 mr-1.5" />
                Ejecutar análisis
              </Button>
            </div>
          )}
        </Card>

        {/* History */}
        {analyses.length > 0 && (
          <Card>
            <CardHeader title="Análisis anteriores" />
            <div className="divide-y divide-gray-50">
              {analyses.map((a) => (
                <button
                  key={a.id}
                  onClick={() => openAnalysis(a.id)}
                  className={`w-full flex items-center justify-between py-3 px-2 text-left hover:bg-gray-50 rounded-lg ${
                    current?.id === a.id ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-700">{formatDate(a.startedAt)}</span>
                    <span className="text-xs text-gray-400">{a.region} · {a.windowDays}d</span>
                  </div>
                  <StatusBadge status={a.status} />
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Current analysis */}
        {current && (
          <Card>
            <CardHeader
              title={`Análisis ${current.id.substring(0, 8)}`}
              subtitle={`${current.region} · ventana ${current.windowDays} días · ${formatDate(current.startedAt)}`}
              action={<StatusBadge status={current.status} />}
            />

            {(current.status === 'RUNNING' || current.status === 'PENDING') && (
              <div className="py-6">
                <div className="flex items-center gap-3 mb-3">
                  <Spinner size="sm" />
                  <span className="text-sm text-gray-600">
                    {current.currentStep} — {current.progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{ width: `${current.progress}%`, background: 'linear-gradient(90deg, #1B2B6B, #5B8DB8)' }}
                  />
                </div>
              </div>
            )}

            {current.status === 'FAILED' && (
              <Alert type="error" title="El análisis falló" message={current.error?.message || 'Error desconocido'} />
            )}

            {current.status === 'COMPLETED' && results && (
              <div className="space-y-5">
                {!results.flowLogsAvailable && (
                  <Alert type="warning" title="Sin datos de Flow Logs" message={results.coverageNote || 'No se encontraron VPC Flow Logs en CloudWatch Logs.'} />
                )}

                {/* Summary tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <SummaryTile label="Instancias EC2" value={results.summary.totalInstances} />
                  <SummaryTile label="Con puertos abiertos" value={results.summary.instancesWithOpenPorts} accent />
                  <SummaryTile label="Puertos sin uso" value={results.summary.totalUnusedOpenPorts} accent />
                  <SummaryTile label="Reglas sugeridas" value={results.summary.totalSuggestedRules} />
                </div>

                {/* Findings */}
                <div className="space-y-2">
                  {results.ec2Findings.length === 0 ? (
                    <p className="text-sm text-gray-500">No se encontraron instancias EC2.</p>
                  ) : (
                    results.ec2Findings
                      .slice()
                      .sort((a, b) => b.openToWorldPorts.length - a.openToWorldPorts.length)
                      .map((f) => <InstanceFinding key={f.instanceId} finding={f} />)
                  )}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}

function SummaryTile({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="rounded-xl border border-gray-100 p-4 bg-white">
      <p className="text-2xl font-bold" style={{ color: accent ? '#E05A1C' : '#1B2B6B' }}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}
